import { useState, useCallback } from "react"
import { toast } from "sonner"
import { Lightbulb, Plus, Loader2, Check, X } from "lucide-react"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { NOTICE_ACT, NOTICE_QUIET } from "@/lib/notice-tier"
import { useStrategyStore } from "@/stores/strategy-store"
import { isRanAndEmpty, useExtractionStore } from "@/stores/extraction-store"
import { WinThemeCard } from "@/components/strategy/WinThemeCard"
import { WinThemeDialog } from "@/components/strategy/WinThemeDialog"
import { WinThemeCoverageBar } from "@/components/strategy/WinThemeCoverageBar"
import { WinThemeCoverageMatrix } from "@/components/strategy/WinThemeCoverageMatrix"
import { WinThemeRefineDialog } from "@/components/strategy/WinThemeRefineDialog"
import { PendingThemeCard } from "@/components/strategy/PendingThemeCard"
import { BankSuggestionTier } from "@/components/strategy/BankSuggestionTier"
import { AddThemeWithAiDialog } from "@/components/strategy/AddThemeWithAiDialog"
import type { WinTheme, ThemeCategory, ThemeStrength, ThemeEvidence, PendingThemeSuggestion } from "@/stores/strategy-store"
import type { ExtractedCriterion } from "@/types/extraction"
import { ViewOnlyNotice } from "@/components/ViewOnlyNotice"

interface WinThemesListProps {
  bidId: string
  /** The reader can't edit this bid (e.g. view-only access): list the themes, offer no changes. */
  readOnly?: boolean
}

// The AI-suggest controls are disabled when there are no evaluation criteria to
// suggest against. The reason is rendered as visible text and linked from both
// controls via aria-describedby, following BidDetailsPage's Analyse hint: a
// `title` alone is dead on arrival here, because the Button base sets
// `disabled:pointer-events-none`, so a disabled button never receives the hover
// that would fire a native tooltip.
const NO_CRITERIA_HINT_ID = 'win-themes-no-criteria-reason'

export function WinThemesList({ bidId, readOnly = false }: WinThemesListProps) {
  const {
    themes,
    isLoadingThemes,
    isSavingTheme,
    isSuggestingThemes,
    coverageMatrix,
    pendingSuggestions,
    bankMatches,
    createTheme,
    updateTheme,
    removeTheme,
    suggestThemes,
    acceptSuggestion,
    acceptAllSuggestions,
    dismissSuggestion,
    discardAllSuggestions,
    refinePendingSuggestion,
    addBankMatchToBid,
    loadCoverageMatrix,
  } = useStrategyStore()

  const evalCriteria = useExtractionStore(
    s => s.categories.evaluation_criteria.items as ExtractedCriterion[]
  )
  const criteriaCategory = useExtractionStore(s => s.categories.evaluation_criteria)

  const hasEvalCriteria = evalCriteria.length > 0

  // Disabled either way — with no criteria there is nothing to suggest against.
  // Only the reason differs: telling a user to "run analysis first" once the run
  // has finished and found none is an instruction they already followed and can
  // never satisfy. `isRanAndEmpty` is deliberately narrower than "looks empty",
  // so mid-run, mid-recovery and errored categories keep the run-first ask.
  const noCriteriaReason = !hasEvalCriteria
    ? isRanAndEmpty(criteriaCategory)
      ? "AI theme suggestions need evaluation criteria — this pack doesn't define any."
      : 'AI theme suggestions need evaluation criteria — run the evaluation criteria analysis first.'
    : null

  const [themeDialogOpen, setThemeDialogOpen] = useState(false)
  const [editingTheme, setEditingTheme] = useState<WinTheme | null>(null)
  const [editingPending, setEditingPending] = useState<PendingThemeSuggestion | null>(null)
  const [prefilledCriteriaIds, setPrefilledCriteriaIds] = useState<string[]>([])
  const [matrixOpen, setMatrixOpen] = useState(false)
  const [refiningTheme, setRefiningTheme] = useState<WinTheme | null>(null)
  const [refineDialogOpen, setRefineDialogOpen] = useState(false)
  const [addWithAiOpen, setAddWithAiOpen] = useState(false)

  const handleCreate = useCallback(async (data: {
    title: string
    description: string | null
    category: ThemeCategory
    strength: ThemeStrength
    evidence: ThemeEvidence[]
    evaluationCriteriaIds: string[]
  }) => {
    await createTheme(bidId, data)
    setThemeDialogOpen(false)
    setEditingPending(null)
    void loadCoverageMatrix(bidId)
  }, [bidId, createTheme, loadCoverageMatrix])

  const handleUpdate = useCallback(async (data: {
    title: string
    description: string | null
    category: ThemeCategory
    strength: ThemeStrength
    evidence: ThemeEvidence[]
    evaluationCriteriaIds: string[]
  }) => {
    if (!editingTheme) return
    await updateTheme(bidId, editingTheme.id, data)
    setEditingTheme(null)
    setThemeDialogOpen(false)
    void loadCoverageMatrix(bidId)
  }, [bidId, editingTheme, updateTheme, loadCoverageMatrix])

  const handleRemove = useCallback(async (themeId: string) => {
    await removeTheme(bidId, themeId)
    void loadCoverageMatrix(bidId)
  }, [bidId, removeTheme, loadCoverageMatrix])

  const handleEdit = useCallback((theme: WinTheme) => {
    setEditingTheme(theme)
    setThemeDialogOpen(true)
  }, [])

  const handleOpenCreate = useCallback(() => {
    setEditingTheme(null)
    setEditingPending(null)
    setPrefilledCriteriaIds([])
    setThemeDialogOpen(true)
  }, [])

  const handleAddThemeForCriterion = useCallback((criterionId: string) => {
    setEditingTheme(null)
    setEditingPending(null)
    setPrefilledCriteriaIds([criterionId])
    setThemeDialogOpen(true)
  }, [])

  const handleViewMatrix = useCallback(() => {
    void loadCoverageMatrix(bidId)
    setMatrixOpen(true)
  }, [bidId, loadCoverageMatrix])

  const handleGenerate = useCallback(() => {
    void suggestThemes(bidId)
  }, [bidId, suggestThemes])

  const handleRefine = useCallback((theme: WinTheme) => {
    setRefiningTheme(theme)
    setRefineDialogOpen(true)
  }, [])

  const handleAcceptPending = useCallback(async (sid: string) => {
    if (!await acceptSuggestion(bidId, sid)) {
      toast.error("Couldn't accept this theme — please try again")
      return
    }
    void loadCoverageMatrix(bidId)
  }, [bidId, acceptSuggestion, loadCoverageMatrix])

  const handleAcceptAll = useCallback(async () => {
    if (!await acceptAllSuggestions(bidId)) {
      toast.error("Couldn't accept these themes — please try again")
      return
    }
    void loadCoverageMatrix(bidId)
  }, [bidId, acceptAllSuggestions, loadCoverageMatrix])

  const handleDismissPending = useCallback((sid: string) => () => {
    void dismissSuggestion(bidId, sid)
  }, [bidId, dismissSuggestion])

  const handleDiscardAll = useCallback(() => {
    void discardAllSuggestions(bidId)
  }, [bidId, discardAllSuggestions])

  const handleRefinePending = useCallback((sid: string) => async (instruction: string) => {
    await refinePendingSuggestion(bidId, sid, instruction)
  }, [bidId, refinePendingSuggestion])

  const handleEditPending = useCallback((pending: PendingThemeSuggestion) => () => {
    // Editing a pending theme opens the manual dialog pre-filled. Accepting from the
    // dialog creates a real WinTheme and the pending entry is dismissed server-side.
    setEditingPending(pending)
    setEditingTheme(null)
    setThemeDialogOpen(true)
  }, [])

  const handleAddBankMatch = useCallback((bankThemeId: string) => {
    void addBankMatchToBid(bidId, bankThemeId).then(() => loadCoverageMatrix(bidId))
  }, [bidId, addBankMatchToBid, loadCoverageMatrix])

  const handleAddWithAiConfirm = useCallback(async (candidate: PendingThemeSuggestion) => {
    await createTheme(bidId, {
      title: candidate.title,
      description: candidate.description || null,
      category: candidate.category as ThemeCategory,
      strength: candidate.strength as ThemeStrength,
      evidence: candidate.evidence.map(e => ({ type: e.type, detail: e.detail })),
      evaluationCriteriaIds: candidate.mappedCriteriaIds,
      sources: candidate.sources && candidate.sources.length > 0 ? candidate.sources : null,
      source: 'ai',
      evidenceStrength: candidate.evidenceStrength,
      libraryEvidenceCount: candidate.libraryEvidenceCount,
    })
    void loadCoverageMatrix(bidId)
  }, [bidId, createTheme, loadCoverageMatrix])

  const handleRefineDialogClose = useCallback((open: boolean) => {
    setRefineDialogOpen(open)
    if (!open) setRefiningTheme(null)
  }, [])

  // Editing-pending → manual create flow. The pending dialog confirm should also
  // dismiss the pending entry server-side so it doesn't double up.
  const handleConfirmFromPendingEdit = useCallback(async (data: {
    title: string
    description: string | null
    category: ThemeCategory
    strength: ThemeStrength
    evidence: ThemeEvidence[]
    evaluationCriteriaIds: string[]
  }) => {
    if (!editingPending) return
    await createTheme(bidId, {
      ...data,
      source: 'ai',
      evidenceStrength: editingPending.evidenceStrength,
      libraryEvidenceCount: editingPending.libraryEvidenceCount,
      sources: editingPending.sources && editingPending.sources.length > 0 ? editingPending.sources : null,
    })
    await dismissSuggestion(bidId, editingPending.id)
    setEditingPending(null)
    setThemeDialogOpen(false)
    void loadCoverageMatrix(bidId)
  }, [bidId, editingPending, createTheme, dismissSuggestion, loadCoverageMatrix])

  // Loading
  if (isLoadingThemes) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const hasPending = pendingSuggestions.length > 0
  const hasBank = bankMatches.length > 0
  // A read-only reader sees accepted themes only (suggestions are for someone to act on).
  const hasContent = readOnly
    ? themes.length > 0
    : themes.length > 0 || hasPending || hasBank || isSuggestingThemes

  // Empty + nothing pending/generating → empty-state hero
  if (!hasContent) {
    return (
      <>
        <div className="py-20 text-center">
          <div className="flex justify-center mb-4">
            <Lightbulb className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="text-base font-medium">Define your win themes</h3>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
            Win themes are the 3–5 key reasons the client should choose you. AI will read your tender and propose the strongest themes.
          </p>
          {readOnly ? (
            <ViewOnlyNotice className="mt-5 justify-center" />
          ) : (
          <>
          <div className="mt-5 flex items-center justify-center gap-3">
            <Button
              onClick={handleGenerate}
              disabled={!hasEvalCriteria || isSuggestingThemes}
              title={noCriteriaReason ?? undefined}
              aria-describedby={noCriteriaReason ? NO_CRITERIA_HINT_ID : undefined}
            >
              <GradientSparkleIcon className="h-4 w-4 mr-2" />
              Generate with AI
            </Button>
            <Button variant="ghost" onClick={handleOpenCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add manually
            </Button>
          </div>
          {noCriteriaReason && (
            <p id={NO_CRITERIA_HINT_ID} className="mt-3 text-meta">
              {noCriteriaReason}
            </p>
          )}
          </>
          )}
        </div>

        <WinThemeDialog
          open={themeDialogOpen}
          onOpenChange={setThemeDialogOpen}
          theme={editingTheme}
          evalCriteria={evalCriteria}
          onConfirm={editingTheme ? handleUpdate : handleCreate}
          isLoading={isSavingTheme}
        />
      </>
    )
  }

  // Has themes / pending / bank — render the full working surface
  return (
    <div className="space-y-4">
      {/* Coverage bar (only when accepted themes exist) */}
      {themes.length > 0 && (
        <WinThemeCoverageBar
          themeCount={themes.length}
          coverage={coverageMatrix}
          onViewMatrix={handleViewMatrix}
          onAddThemeForCriterion={readOnly ? undefined : handleAddThemeForCriterion}
        />
      )}

      {/* Generating state — show a single loading banner if no pending content yet */}
      {isSuggestingThemes && !hasPending && (
        // Nothing to act on while it generates, so no accent edge — only the
        // spinner carries it. The bar this turns into DOES want the user.
        <div className={cn(NOTICE_QUIET, 'flex items-center gap-3')}>
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[hsl(var(--brand))]" />
          <div className="flex-1">
            <div className="text-sm font-medium">AI is proposing your win themes…</div>
            <div className="text-meta mt-0.5">Reading your tender, evaluation criteria, and library evidence.</div>
          </div>
        </div>
      )}

      {/* Sticky pending bar — only when there are pending suggestions */}
      {hasPending && !readOnly && (
        <div className={cn(NOTICE_ACT, 'flex items-center justify-between gap-3')}>
          <div className="flex items-center gap-3 min-w-0">
            <GradientSparkleIcon solid className="h-4 w-4 shrink-0 text-[hsl(var(--ai-accent-text))]" />
            <div>
              <div className="text-sm font-medium">
                AI proposed {pendingSuggestions.length} theme{pendingSuggestions.length === 1 ? '' : 's'} for your review
              </div>
              <div className="text-meta mt-0.5">Accept the ones you want to keep — dismiss the rest.</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={handleDiscardAll}>
              <X className="h-3.5 w-3.5 mr-1" />
              Discard all
            </Button>
            <Button
              size="sm"
              onClick={() => void handleAcceptAll()}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Accept all {pendingSuggestions.length}
            </Button>
          </div>
        </div>
      )}

      {/* Actions — only when accepted themes exist (Add with AI + Add manually) */}
      {themes.length > 0 && !readOnly && (
        <div className="flex items-center justify-end gap-2">
          {noCriteriaReason && (
            <p id={NO_CRITERIA_HINT_ID} className="mr-auto text-meta">
              {noCriteriaReason}
            </p>
          )}
          <Button
            variant="ai-ghost"
            size="sm"
            onClick={() => setAddWithAiOpen(true)}
            disabled={!hasEvalCriteria}
            title={noCriteriaReason ?? undefined}
            aria-describedby={noCriteriaReason ? NO_CRITERIA_HINT_ID : undefined}
          >
            <GradientSparkleIcon className="h-3.5 w-3.5 mr-1.5" />
            Add with AI
          </Button>
          <Button variant="ghost" size="sm" onClick={handleOpenCreate}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add manually
          </Button>
        </div>
      )}

      {/* Pending suggestion cards */}
      {hasPending && !readOnly && (
        <div className="space-y-3">
          {pendingSuggestions.map((pending, i) => (
            <PendingThemeCard
              key={pending.id}
              pending={pending}
              index={themes.length + i}
              onAccept={() => void handleAcceptPending(pending.id)}
              onDismiss={handleDismissPending(pending.id)}
              onEdit={handleEditPending(pending)}
              onRefine={handleRefinePending(pending.id)}
            />
          ))}
        </div>
      )}

      {/* Accepted theme cards */}
      {themes.length > 0 && (
        <div className="space-y-4">
          {themes.map((theme, i) => (
            <WinThemeCard
              key={theme.id}
              theme={theme}
              index={i}
              evalCriteria={evalCriteria}
              onEdit={() => handleEdit(theme)}
              onRemove={() => void handleRemove(theme.id)}
              onRefine={() => handleRefine(theme)}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}

      {/* Bank suggestion tier */}
      {hasBank && !readOnly && <BankSuggestionTier matches={bankMatches} onAdd={handleAddBankMatch} />}

      {/* Dialogs */}
      <WinThemeDialog
        open={themeDialogOpen}
        onOpenChange={(open) => {
          setThemeDialogOpen(open)
          if (!open) {
            setEditingTheme(null)
            setEditingPending(null)
            setPrefilledCriteriaIds([])
          }
        }}
        theme={editingTheme}
        prefill={editingPending ? {
          title: editingPending.title,
          description: editingPending.description,
          category: editingPending.category as ThemeCategory,
          strength: editingPending.strength as ThemeStrength,
          evidence: editingPending.evidence,
          evaluationCriteriaIds: editingPending.mappedCriteriaIds,
        } : undefined}
        prefilledCriteriaIds={prefilledCriteriaIds}
        evalCriteria={evalCriteria}
        onConfirm={editingTheme ? handleUpdate : (editingPending ? handleConfirmFromPendingEdit : handleCreate)}
        isLoading={isSavingTheme}
      />

      {coverageMatrix && (
        <WinThemeCoverageMatrix
          open={matrixOpen}
          onOpenChange={setMatrixOpen}
          coverage={coverageMatrix}
          themes={themes}
        />
      )}

      {refiningTheme && (
        <WinThemeRefineDialog
          open={refineDialogOpen}
          onOpenChange={handleRefineDialogClose}
          theme={refiningTheme}
          bidId={bidId}
        />
      )}

      <AddThemeWithAiDialog
        bidId={bidId}
        open={addWithAiOpen}
        onOpenChange={setAddWithAiOpen}
        onConfirm={handleAddWithAiConfirm}
      />
    </div>
  )
}
